import Vue from 'vue'
import { getterTree, mutationTree, actionTree } from 'typed-vuex'
import { setState } from '~/lib/utils/store'

export const SCHOLARSHIP_LIST = 'scholarshipList'
export const APPLICATION_LIST = 'applicationList'

interface StoreName {
  storeName: typeof SCHOLARSHIP_LIST | typeof APPLICATION_LIST
}

interface ResourceLocator extends StoreName {
  id: number | string;
}

interface StoreList extends StoreName {
  list: (Scholarship.General | Scholarship.GeneralSent)[]
}

interface StoreItem extends StoreName {
  item: Scholarship.General | Partial<Scholarship.GeneralSent>;
}

interface StoreProperty extends ResourceLocator {
  propName: keyof Scholarship.General | keyof Scholarship.GeneralSent | keyof Scholarship.Similar;
  propValue: (Scholarship.General | Scholarship.GeneralSent)[keyof (Scholarship.General | Scholarship.GeneralSent)]
}

export const state = () => ({
  [SCHOLARSHIP_LIST]: {} as { [key: string]: Scholarship.General },
  [APPLICATION_LIST]: {} as { [key: string]: Scholarship.GeneralSent },
  isLoaderActive: false
})

export const getters = getterTree(
  state,
  {
    applicationCount(_state, getters) {
      return getters.applications.length
    },
    currentCard(state, getters, rootState) {
      if (!rootState.route.params || !rootState.route.params.cardId) { return null }

      return state[SCHOLARSHIP_LIST][rootState.route.params.cardId]
    },
    scholarships(state) {
      const list = state[SCHOLARSHIP_LIST]
      return list ? Object.values(list) : []
    },
    applications(state) {
      const list = state[APPLICATION_LIST]
      return list ? Object.values(list) : []
    },
    getListItem(state) {
      return ({ storeName, id }: ResourceLocator) => {
        if (state[storeName] && state[storeName][id]) {
          return state[storeName][id] as Scholarship.General
        }
      }
    },
    getLoaderState(state): boolean {
      return state.isLoaderActive
    }
  }
)

export const mutations = mutationTree(
  state,
  {
    setState,
    removeListItem(state, { storeName, id }: ResourceLocator) {
      Vue.delete(state[storeName], id)
    },
    setLoaderState(state, { loader }): void {
      state.isLoaderActive = loader
    }
  }
)

export const actions = actionTree(
  { state, getters, mutations },
  {
    fetchList({ dispatch, commit }, { storeName }: StoreName): Promise<any> {
      commit('setLoaderState', { loader: true })
      return this.$api.scholarship[storeName]().then((res: Axios.AxiosResponse) => {
        return dispatch('setList', { storeName, list: res.data.data })
      })
    },

    setList({ commit }, { storeName, list }: StoreList): { [key: string]: Scholarship.General | Scholarship.GeneralSent } {
      if (storeName === SCHOLARSHIP_LIST) {
        (list as Scholarship.General[]).forEach((el) => {
          el.isReported = 0
        })
      }

      const result = list.reduce((acc, item) => {
        acc[item.scholarshipId] = item

        return acc
      }, {} as {[key: string]: (Scholarship.General | Scholarship.GeneralSent)})

      commit('setState', { stateName: storeName, data: result })
      commit('setLoaderState', { loader: false })
      return result
    },
    setListItem({ state, commit }, { storeName, item }: StoreItem): void {
      if (state[storeName][item.scholarshipId.toString()]) { return }

      commit('setState', { stateName: `${storeName}.${item.scholarshipId}`, data: item })
    },
    removeListItem({ commit }, { storeName, id }: ResourceLocator): void {
      commit('removeListItem', { storeName, id })
    },
    changeItemProperty({ rootState, state, commit }, { storeName, id, propName, propValue }: StoreProperty): void {
      if (state[storeName][id]) {
        commit('setState', { stateName: `${storeName}.${id}.${propName}`, data: propValue })
      }
    },
    updateApplicationCount({ dispatch, getters }, { scholarshipId, number, isOptional }): void {
      const card = this.app.$accessor.cardList.getListItem({ storeName: SCHOLARSHIP_LIST, id: scholarshipId })
      if (!card) { return }

      const newVal = card.requirementApplicationCount + number

      dispatch('cardList/changeItemProperty', {
        storeName: SCHOLARSHIP_LIST,
        id: scholarshipId,
        propName: 'requirementApplicationCount',
        propValue: newVal
      }, { root: true })

      if (isOptional) {
        dispatch('cardList/changeItemProperty', {
          storeName: SCHOLARSHIP_LIST,
          id: scholarshipId,
          propName: 'requirementApplicationCount',
          propValue: newVal
        }, { root: true })
      }
    }
  }
)
